import type { CompTopicExampleRow } from "@nexus/contracts";
import type { CompTopicDetailContext } from "./compTopicDetailContext.js";
import { formatCompactNumber } from "./formatCompactNumber.js";

const STANDARD_MAX_DECIMALS = 2;

function formatSignedDecimal(value: number, maxFractionDigits: number): string {
  const factor = 10 ** maxFractionDigits;
  const rounded = Math.round(value * factor) / factor;
  if (rounded === 0) return "0";
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  });
}

/**
 * Scale winsorized-normalized Days/Assets ([-1, 1]) to the chart-dot percent
 * (e.g. 0.1 → 10). `null` stays `null` so a missing baseline is distinct from 0.
 */
export function scaleCompDaysAssets(daysAssets: number | null): number | null {
  return daysAssets == null ? null : Math.round(daysAssets * 1000) / 10;
}

/** Chart-dot Days/Assets display; `null` ⇒ em dash. */
export function formatCompDaysAssetsRatio(daysAssets: number | null): string {
  const scaled = scaleCompDaysAssets(daysAssets);
  return scaled == null ? "—" : formatCompactNumber(scaled);
}

export function formatCompAssetPct(assetPct: number): string {
  return `${Math.round(assetPct * 100)}%`;
}

export function formatCompAdgeScore(score: number): string {
  return formatSignedDecimal(score, STANDARD_MAX_DECIMALS);
}

export function resolveCompAdgeScore(examples: CompTopicExampleRow[] | undefined): string {
  if (!examples?.length) return "—";
  const score = examples[0]?.adgeScore;
  return score != null ? formatCompAdgeScore(score) : "—";
}

export interface CompTopicDetailPerformanceCard {
  id: "days" | "assets" | "assetPct" | "daysAssets" | "adgeScore";
  labelKey: string;
  value: string;
}

export function buildCompTopicDetailPerformanceCards(
  context: CompTopicDetailContext,
  examples: CompTopicExampleRow[] | undefined,
): CompTopicDetailPerformanceCard[] {
  const { Days, Assets, assetPct, daysAssets } = context.chartMetrics;

  return [
    { id: "days", labelKey: "overview.compTopicDetail.metricDays", value: formatCompactNumber(Days) },
    { id: "assets", labelKey: "overview.compTopicDetail.metricAssets", value: formatCompactNumber(Assets) },
    { id: "assetPct", labelKey: "overview.compTopicDetail.metricAssetPct", value: formatCompAssetPct(assetPct) },
    { id: "daysAssets", labelKey: "overview.compTopicDetail.metricDaysAssets", value: formatCompDaysAssetsRatio(daysAssets) },
    { id: "adgeScore", labelKey: "overview.compTopicDetail.metricAdgeScore", value: resolveCompAdgeScore(examples) },
  ];
}
